// api-error.interceptor.ts
import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, LOCALE_ID } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, EMPTY, throwError } from 'rxjs';
import { AlertService } from '@app/core/services/alert.service';
import { SUPPRESS_UNAUTHORIZED_REDIRECT } from '@app/core/interceptor/http-context-tokens';
import { LoadingService } from '@app/core/services/loading.service';
import { flattenErrors } from '@app/core/utils/flatten-errors';
import { ModalService } from '@app/core/services/modal.service';

function isCancellation(err: unknown): boolean {
  if (err instanceof HttpErrorResponse) {
    if (err.status === 0) {
      const name = (err.error?.name ?? '').toString();
      const msg  = (err.message ?? '').toLowerCase();
      if (name === 'AbortError' || name === 'CanceledError' || msg.includes('abort')) return true;
    }
  } else if (err && typeof err === 'object') {
    const anyErr = err as any;
    if (anyErr.name === 'AbortError' || anyErr.name === 'CanceledError') return true;
  }
  return false;
}

/**
 * ApiErrorInterceptor is responsible for global request error handling
 */
export const ApiErrorInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const router = inject(Router);
  const alertService = inject(AlertService);
  const loadingService = inject(LoadingService);
  const modalService = inject(ModalService);
  const locale = inject(LOCALE_ID);

  // Capture the current route signature (path + query) at the moment the request is dispatched
  const requestOriginUrl = router.url;

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Ignore cancellation/abort like errors
      if (isCancellation(error)) {
        loadingService.stop();
        return EMPTY;
      }

      // If the user navigated away since the request was made, suppress UI errors for this response
      if (router.url !== requestOriginUrl) {
        loadingService.stop();
        return EMPTY;
      }

      const errorArea = modalService.modalState() === 'modal_opened' ? 'modal' : 'header';

      // 400 Bad Request
      if (error.status === 400) {
        alertService.open(errorArea, 'danger', error.error.errors ? flattenErrors(error.error, locale) : 'common.error.unknown', true);
      }

      // 401 Unauthorized
      if (error.status === 401) {
        // Some callers intentionally handle unauthenticated responses themselves.
        if (req.context.get(SUPPRESS_UNAUTHORIZED_REDIRECT)) {
          loadingService.stop();
          return throwError(() => error);
        }

        alertService.open(errorArea, 'danger', 'error.authentication.denied', true);
        setTimeout(() => {
          void router.navigate(['/auth'], { queryParams: { returnUrl: requestOriginUrl } });
        }, 1500);
      }

      // 403 Forbidden
      if (error.status === 403) {
        alertService.open(errorArea, 'danger', 'error.authentication.forbidden', true);
      }

      // 404 Not Found
      if (error.status === 404) {
        alertService.open(errorArea, 'danger', 'common.error.notFound', true);
      }

      // 409 Conflict
      if (error.status === 409) {
        alertService.open(errorArea, 'danger', error.error?.detail ?? 'common.error.unknown', true);
      }

      // 500 Internal Server Error
      if (error.status === 500) {
        alertService.open(errorArea, 'danger', ['error.server.internal', error.message], true);
      }

      // Attach the originating URL signature to the error for downstream consumers/logging
      (error as any).pageSignature = requestOriginUrl;

      loadingService.stop();
      return throwError(() => error);
    })
  );
};
